import { useCallback, useEffect, useMemo, useState } from 'react';
import { BellOutlined, LockOutlined, NotificationOutlined } from '@ant-design/icons';
import { ProCard } from '@ant-design/pro-components';
import { App, Modal, Radio, Space, Typography } from 'antd';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    appVersionUpdateCreate,
    appVersionUpdateDetail,
    appVersionUpdatePatch,
    type AppVersionUpdateType,
} from '@/services/app-version-updates';

import ChannelConfigSection from './ChannelConfigSection';
import I18nContentTabs from './I18nContentTabs';
import type { AppVersionFormValues, I18nLocale, ModalMode } from '../types';
import { CHANNEL_KEYS } from '../types';
import {
    defaultFormValues,
    formValuesToPayload,
    recordToFormValues,
    validateFormValues,
} from '../utils';

import './PublishEditModal.less';

export type PublishEditModalProps = {
    open: boolean;
    mode: ModalMode;
    recordId?: string;
    onClose: () => void;
    onSuccess: () => void;
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <span className="section-title">
        <span className="section-title__text">{children}</span>
    </span>
);

type SectionCardProps = {
    titleId: string;
    descId?: string;
    children: React.ReactNode;
};

const SectionCard: React.FC<SectionCardProps> = ({ titleId, descId, children }) => (
    <ProCard
        className="section-card"
        title={
            <SectionTitle>
                <FormattedMessage id={titleId} />
            </SectionTitle>
        }
    >
        {descId ? (
            <Typography.Paragraph className="section-desc">
                <FormattedMessage id={descId} />
            </Typography.Paragraph>
        ) : null}
        {children}
    </ProCard>
);

const PublishEditModal: React.FC<PublishEditModalProps> = ({
    open,
    mode,
    recordId,
    onClose,
    onSuccess,
}) => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formValues, setFormValues] = useState<AppVersionFormValues>(defaultFormValues());
    const [activeLocale, setActiveLocale] = useState<I18nLocale>('zh');

    const title = useMemo(
        () =>
            mode === 'create'
                ? formatMessage({ id: 'appVersionUpdates.modal.createTitle' })
                : formatMessage({ id: 'appVersionUpdates.modal.editTitle' }, { id: recordId ?? '' }),
        [formatMessage, mode, recordId],
    );

    const resetForm = useCallback(() => {
        setFormValues(defaultFormValues());
        setActiveLocale('zh');
    }, []);

    useEffect(() => {
        if (!open) return;

        if (mode === 'create') {
            resetForm();
            return;
        }

        if (!recordId) return;

        let cancelled = false;
        setLoading(true);
        appVersionUpdateDetail(recordId)
            .then(({ data }) => {
                if (cancelled) return;
                if (data.code !== 200 || !data.data) {
                    message.error(data.msg || formatMessage({ id: 'appVersionUpdates.message.loadFailed' }));
                    return;
                }
                setFormValues(recordToFormValues(data.data));
            })
            .catch((error: any) => {
                if (cancelled) return;
                message.error(error?.response?.data?.msg || error?.message || formatMessage({ id: 'appVersionUpdates.message.loadFailed' }));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [formatMessage, message, mode, open, recordId, resetForm]);

    const updateType = useCallback((type: AppVersionUpdateType) => {
        setFormValues((prev) => ({ ...prev, type }));
    }, []);

    const updateChannel = useCallback(
        (channel: (typeof CHANNEL_KEYS)[number], patch: Partial<AppVersionFormValues[typeof channel]>) => {
            setFormValues((prev) => ({
                ...prev,
                [channel]: { ...prev[channel], ...patch },
            }));
        },
        [],
    );

    const updateContent = useCallback((locale: I18nLocale, text: string) => {
        setFormValues((prev) => ({
            ...prev,
            contents: { ...prev.contents, [locale]: text },
        }));
    }, []);

    const handleSubmit = useCallback(async () => {
        const validationError = validateFormValues(formValues, formatMessage);
        if (validationError) {
            message.warning(validationError);
            return;
        }

        setSubmitting(true);
        try {
            const payload = formValuesToPayload(formValues);
            const response =
                mode === 'create'
                    ? await appVersionUpdateCreate(payload)
                    : await appVersionUpdatePatch(recordId!, payload);
            const { data } = response;
            if (data.code !== 200) {
                message.error(data.msg || formatMessage({ id: 'appVersionUpdates.message.saveFailed' }));
                return;
            }
            message.success(
                formatMessage({
                    id: mode === 'create' ? 'appVersionUpdates.message.createSuccess' : 'appVersionUpdates.message.updateSuccess',
                }),
            );
            onSuccess();
            onClose();
        } catch (error: any) {
            message.error(error?.response?.data?.msg || error?.message || formatMessage({ id: 'appVersionUpdates.message.saveFailed' }));
        } finally {
            setSubmitting(false);
        }
    }, [formValues, formatMessage, message, mode, onClose, onSuccess, recordId]);

    return (
        <Modal
            open={open}
            title={title}
            width={860}
            destroyOnClose
            confirmLoading={submitting}
            okText={<FormattedMessage id="appVersionUpdates.modal.submit" />}
            cancelText={<FormattedMessage id="b.cancel" />}
            className="app-version-modal-root"
            styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', padding: 12 } }}
            onCancel={onClose}
            onOk={handleSubmit}
        >
            <div className="app-version-modal">
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <SectionCard titleId="appVersionUpdates.form.updateType">
                        <Radio.Group
                            value={formValues.type}
                            disabled={loading}
                            className="type-grid"
                            onChange={(event) => updateType(event.target.value)}
                        >
                            <Radio
                                value="Remind"
                                className={classNames('type-tile', {
                                    'type-tile--selected': formValues.type === 'Remind',
                                })}
                            >
                                <Typography.Text strong className="type-tile__title">
                                    <BellOutlined style={{ marginRight: 6 }} />
                                    <FormattedMessage id="appVersionUpdates.type.Remind.title" />
                                </Typography.Text>
                                <Typography.Paragraph type="secondary" className="type-tile__desc">
                                    <FormattedMessage id="appVersionUpdates.type.Remind.desc" />
                                </Typography.Paragraph>
                            </Radio>
                            <Radio
                                value="Force"
                                className={classNames('type-tile', {
                                    'type-tile--selected': formValues.type === 'Force',
                                })}
                            >
                                <Typography.Text strong className="type-tile__title">
                                    <LockOutlined style={{ marginRight: 6 }} />
                                    <FormattedMessage id="appVersionUpdates.type.Force.title" />
                                </Typography.Text>
                                <Typography.Paragraph type="secondary" className="type-tile__desc">
                                    <FormattedMessage id="appVersionUpdates.type.Force.desc" />
                                </Typography.Paragraph>
                            </Radio>
                            <Radio
                                value="RemindOnly"
                                className={classNames('type-tile', {
                                    'type-tile--selected': formValues.type === 'RemindOnly',
                                })}
                            >
                                <Typography.Text strong className="type-tile__title">
                                    <NotificationOutlined style={{ marginRight: 6 }} />
                                    <FormattedMessage id="appVersionUpdates.type.RemindOnly.title" />
                                </Typography.Text>
                                <Typography.Paragraph type="secondary" className="type-tile__desc">
                                    <FormattedMessage id="appVersionUpdates.type.RemindOnly.desc" />
                                </Typography.Paragraph>
                            </Radio>
                        </Radio.Group>
                        <div className="hint-box">
                            <FormattedMessage id="appVersionUpdates.type.clickHint" />
                        </div>
                    </SectionCard>

                    <SectionCard
                        titleId="appVersionUpdates.form.channels"
                        descId="appVersionUpdates.form.channelsHint"
                    >
                        <div className="channel-stack">
                            {CHANNEL_KEYS.map((channel) => (
                                <ChannelConfigSection
                                    key={channel}
                                    channel={channel}
                                    enabled={formValues[channel].enabled}
                                    versionName={formValues[channel].version_name}
                                    downloadUrl={formValues[channel].download_url}
                                    disabled={loading}
                                    onEnabledChange={(enabled) => updateChannel(channel, { enabled })}
                                    onVersionChange={(version_name) => updateChannel(channel, { version_name })}
                                    onUrlChange={(download_url) => updateChannel(channel, { download_url })}
                                />
                            ))}
                        </div>
                    </SectionCard>

                    <SectionCard
                        titleId="appVersionUpdates.form.i18n"
                        descId="appVersionUpdates.form.i18nHint"
                    >
                        <I18nContentTabs
                            value={formValues.contents}
                            disabled={loading}
                            activeKey={activeLocale}
                            onActiveKeyChange={setActiveLocale}
                            onChange={updateContent}
                        />
                    </SectionCard>
                </Space>
            </div>
        </Modal>
    );
};

export default PublishEditModal;
